/**
 * The unsaved-changes guard for an editor in a dialog (TMM-B5, FRM-008, DLG-005). Closing a dirty
 * dialog — Cancel, `Esc`, the backdrop, a ✕ — does not close it: the dialog's content is
 * replaced, in place, by the discard question (`DialogGuardBody`), never a second dialog on top.
 * "Keep editing" (also `Esc` or the backdrop while the question shows) brings the form back with
 * every value, and focus where it was; "Discard changes" closes the dialog. A clean dialog closes at once. A route navigation while the dialog is dirty
 * is held by `useUnsavedGuard` and asks the same question in the same place — any history move,
 * including Back / Forward between tabs of one page (a query-only change, TMMAPP-0081) — but never
 * the page's own `useUrlState` replace (a debounced search, a stale filter cleaned up on load,
 * TMMAPP-0082): that is not the person leaving.
 *
 * While `busy` (the save is in flight) a close request is ignored — the dialog cannot be
 * dismissed into a half-saved state — exactly as `ConfirmDialog` behaves. `errorTrigger` is for
 * a dialog whose errors arrive as props: bumping it focuses the first error.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useErrorFocus } from './useErrorFocus';
import { useUnsavedGuard } from './useUnsavedGuard';

export interface DialogDirtyGuard {
  /** The discard question is showing in place of the form. */
  discarding: boolean;
  /** Cancel, `Esc`, backdrop and ✕ all call this. */
  requestClose: () => void;
  keepEditing: () => void;
  discard: () => void;
  /** The discard question's title / consequence ids, for the host dialog's aria attributes. */
  titleId: string;
  consequenceId: string;
  /** Spread on the host `Dialog`: routes backdrop / `Esc` to the guard and labels the question. */
  dialogProps: (formTitleId?: string) => {
    onClose: () => void;
    'aria-labelledby': string | undefined;
    /** Only while the question shows; the form's own description is the caller's. */
    'aria-describedby'?: string;
  };
  /** For `DialogGuardBody`: wraps the form so it stays mounted (values kept) while hidden. */
  attachBody: (element: HTMLDivElement | null) => void;
  /** After setting field errors: focus the summary, the first invalid field, or the alert. */
  focusErrors: () => void;
}

function firstControl(root: HTMLElement | null): HTMLElement | null {
  return (
    root?.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), [role="combobox"], button:not(:disabled)',
    ) ?? null
  );
}

export function useDialogDirtyGuard(
  dirty: boolean,
  onClose: () => void,
  busy = false,
  errorTrigger = 0,
  /** The dialog is showing. A dialog that stays mounted while closed passes its `open`. */
  open = true,
): DialogDirtyGuard {
  const titleId = useId();
  const consequenceId = useId();
  // While open and clean, another editor's held navigation closes this dialog before that editor
  // asks (TMMAPP-0097 §C 26): the page's question never opens on top of it.
  const latestClose = useRef(onClose);
  useEffect(() => {
    latestClose.current = onClose;
  });
  const dismissWhenClean = useCallback(() => latestClose.current(), []);
  // A dirty dialog holds EVERY history move, a query-only one included (Back from `?tab=rules` to
  // `?tab=register` unmounts the tab and its dialog with it). That is safe for a clean page: only a
  // dialog with unsaved work is a source, and behind a modal nothing else changes the query.
  const nav = useUnsavedGuard(dirty, {
    busy,
    includeSearch: true,
    dismissWhenClean: open ? dismissWhenClean : undefined,
  });
  const [confirming, setConfirming] = useState(false);
  const { rootRef: attachBody, getRoot, focusErrors } = useErrorFocus<HTMLDivElement>(errorTrigger);
  const lastFocus = useRef<HTMLElement | null>(null);
  // Set by "Keep editing" only: after a Discard the dialog is closing, and focus belongs to the
  // opener (MUI's focus trap returns it), never to a control inside the closing content.
  const returning = useRef(false);
  const discarding = confirming || nav.blocked;

  // Back from the question: focus returns to the control that had it (the form never unmounted).
  useEffect(() => {
    if (discarding || !returning.current) return;
    returning.current = false;
    const target = lastFocus.current?.isConnected ? lastFocus.current : firstControl(getRoot());
    target?.focus();
  }, [discarding, getRoot]);

  const remember = useCallback(() => {
    const active = document.activeElement;
    lastFocus.current =
      active instanceof HTMLElement && getRoot()?.contains(active) ? active : null;
  }, [getRoot]);

  const keepEditing = useCallback(() => {
    returning.current = true;
    setConfirming(false);
    nav.stay();
  }, [nav]);

  // `Esc` and the backdrop on the question are its dismiss: keep editing (as in a ConfirmDialog).
  const requestClose = useCallback(() => {
    if (busy) return;
    if (discarding) {
      keepEditing();
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    remember();
    setConfirming(true);
  }, [busy, discarding, dirty, onClose, remember, keepEditing]);

  // A held route change continues, and the dialog closes either way: its host may survive the
  // navigation (a dialog in the shell, a param-only route change) and must not keep the values.
  const discard = useCallback(() => {
    setConfirming(false);
    if (nav.blocked) nav.proceed();
    onClose();
  }, [nav, onClose]);

  const dialogProps = useCallback(
    (formTitleId?: string) => ({
      onClose: requestClose,
      'aria-labelledby': discarding ? titleId : formTitleId,
      ...(discarding ? { 'aria-describedby': consequenceId } : {}),
    }),
    [requestClose, discarding, titleId, consequenceId],
  );

  return {
    discarding,
    requestClose,
    keepEditing,
    discard,
    titleId,
    consequenceId,
    dialogProps,
    attachBody,
    focusErrors,
  };
}
